import React, { createContext, useContext } from 'react';
import {
	useCountryCodes,
	useEmailContactDetails,
	usePersonCommunicationData,
	usePersonCommunicationLoader,
	usePhoneContactDetails,
	useServiceAuthorization,
	useUpdateContact,
} from '@gov-nx/core/hooks';
import { useContactHistory } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import type { PageCode } from '../../definitions/codes';
import type { ContactDataContext } from './context.types';
import { useNuContactQueries } from './hooks/useNuContactsQueries';

const AUTHORIZATION_SERVICE_NAME = 'PODANI_KONTAKTNI_UDAJE_ROB';

const NastaveniKontaktniUdajeContext =
	createContext<Nullable<ContactDataContext>>(null);

interface NastaveniKontaktniUdajeContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	isContactHistoryEnabled: boolean;
	isNuDataEnabled: boolean;
}

export function NastaveniKontaktniUdajeContextProvider({
	children,
	code,
	isContactHistoryEnabled,
	isNuDataEnabled,
}: NastaveniKontaktniUdajeContextProviderProps) {
	const { t } = useLocale(code);
	const communicationData = usePersonCommunicationData();
	const { stringToPhoneShape } = useCountryCodes();
	const { refetchPersonCommunication, isReloading } =
		usePersonCommunicationLoader();

	const countryCodes = useCountryCodes();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const authContext = useAutorizaceContext();

	const onAuthorization = (
		autorizaceId: string,
		nonHighAuthorizationFlow: () => void
	) => {
		if (authContext) {
			authContext.onInitAuthorization(
				AUTHORIZATION_SERVICE_NAME,
				autorizaceId,
				nonHighAuthorizationFlow
			);
		} else {
			nonHighAuthorizationFlow();
		}
	};

	const contactsHistory = useContactHistory({
		isEnabled: isContactHistoryEnabled,
	});

	const { nuEmailQuery, nuPhoneQuery } = useNuContactQueries({
		isNuDataEnabled,
	});

	const nuEmail =
		!communicationData.emailVerified && !communicationData.unverifiedEmail
			? nuEmailQuery.data?.hodnota
			: undefined;
	const nuPhone =
		!communicationData.phoneVerified && !communicationData.unverifiedPhone
			? nuPhoneQuery.data?.hodnota
			: undefined;

	const updateContact = useUpdateContact({
		source: { source: 'ROB' },
		unverifiedEmail: communicationData.unverifiedEmail,
		unverifiedPhone: communicationData.unverifiedPhone,
	});

	const emailContactDetails = useEmailContactDetails({
		updateContact: updateContact.email,
		isEditing: communicationData.control.isEditingEmail,
		refetch: refetchPersonCommunication,
		isReloading,
		unverifiedEmail: communicationData.unverifiedEmail,
		emailVerified: communicationData.emailVerified,
		tooltipText: t('kontakt.contact-info-tooltip'),
		isLowAuthorization,
		isAuthorizationEnforced,
		onAuthorization,
	});

	const phoneContactDetails = usePhoneContactDetails({
		updateContact: updateContact.phone,
		refetch: refetchPersonCommunication,
		isEditing: communicationData.control.isEditingPhone,
		isReloading,
		countryCodes,
		unverifiedPhone: communicationData.unverifiedPhone,
		phoneVerified: communicationData.phoneVerified,
		tooltipText: t('kontakt.contact-info-tooltip'),
		isLowAuthorization,
		isAuthorizationEnforced,
		onAuthorization,
	});

	const handleUseEmail = (email: string) => {
		emailContactDetails.formDefinition.formMethods.setValue('email', email);
		emailContactDetails.onSubmit();
	};

	const handleUsePhone = (phone: string) => {
		const shape = stringToPhoneShape(phone);
		if (shape) {
			phoneContactDetails.formDefinition.formMethods.setValue('telefon', shape);
			phoneContactDetails.onSubmit();
		}
	};

	return (
		<NastaveniKontaktniUdajeContext.Provider
			value={{
				initialState: {
					isLoading:
						!countryCodes.isFetched ||
						nuEmailQuery.isFetching ||
						nuPhoneQuery.isFetching ||
						contactsHistory.isFetching,
					error: countryCodes.error ?? undefined,
				},
				email: emailContactDetails,
				phone: phoneContactDetails,
				nuEmail: nuEmail
					? {
							email: nuEmail,
							isLoading: emailContactDetails.isUpdating,
							useIt: () => handleUseEmail(nuEmail),
						}
					: undefined,
				nuPhone: nuPhone
					? {
							phone: nuPhone,
							useIt: () => handleUsePhone(nuPhone),
							isLoading: phoneContactDetails.isUpdating,
						}
					: undefined,
				historicalEmail: contactsHistory.latestEmail
					? {
							email: contactsHistory.latestEmail,
							isLoading: emailContactDetails.isUpdating,
							useIt: () =>
								contactsHistory.latestEmail &&
								handleUseEmail(contactsHistory.latestEmail),
						}
					: undefined,
				historicalPhone: contactsHistory.latestPhone
					? {
							phone: contactsHistory.latestPhone,
							useIt: () =>
								contactsHistory.latestPhone &&
								handleUsePhone(contactsHistory.latestPhone),
							isLoading: phoneContactDetails.isUpdating,
						}
					: undefined,
			}}>
			{children}
		</NastaveniKontaktniUdajeContext.Provider>
	);
}

export const useNastaveniKontaktnichUdajuContextInstance =
	(): ContactDataContext =>
		useContext(NastaveniKontaktniUdajeContext) as ContactDataContext;
